import React from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { ChevronIcon } from 'components/Utils/Icons'

export const ROW_HEIGHT = 44
const PADDING_LEFT_LEVEL_RATIO = 20

const ENTER = 'Enter'
const SHIFT = 'Shift'
const TAB = 'Tab'
const UP = 'ArrowUp'
const DOWN = 'ArrowDown'
const RIGHT = 'ArrowRight'
const LEFT = 'ArrowLeft'

const NAV_KEYS = [ENTER, SHIFT, TAB, UP, DOWN, RIGHT, LEFT]

export default function Entry({
  id,
  entryKey,
  depth,
  title,
  icon,
  detail = '',
  isExpandable,
  onSelectData,
  renderBypass,

  isSelected,
  isFocused,
  isExpanded,

  onBlur,
  onSelect,
  onToggleExpand,
  onFocusNextRow,
  onFocusPreviousRow,
  tabIndex,
}) {
  const ref = React.useRef()

  React.useEffect(
    () => {
      if (isFocused) {
        ref.current.focus()
        ref.current.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'center',
        })
      }
    },
    [isFocused]
  )

  if (renderBypass) {
    return renderBypass()
  }

  return (
    <div
      ref={ref}
      className={classNames(
        'flexbox flexbox-align-center flexbox-row',
        'padding-horizontal-normal',
        'pointer no-outline',
        'shade-05 font-weight-semibold',
        'is-focused transition',
        'fs-block data-hj-suppress' /* fs-block & data-hj-suppress hides content for FullStory & Hotjar */,
        'background-shade-50-on-hover background-shade-45-on-focus',
        {
          'background-shade-45': isSelected,
        }
      )}
      tabIndex={tabIndex}
      onKeyDown={event => {
        if (NAV_KEYS.includes(event.key)) {
          if (event.key === ENTER) {
            event.preventDefault()
            onSelect({ id, onSelectData })
            return
          }
          if (isExpandable && (event.key === LEFT || event.key === RIGHT)) {
            onToggleExpand(id)
          }

          if (event.key === DOWN || (event.key === TAB && !event.shiftKey)) {
            event.preventDefault()
            onFocusNextRow()
            return
          }
          if (event.key === UP || (event.key === TAB && event.shiftKey)) {
            event.preventDefault()
            onFocusPreviousRow()
            return
          }
          return
        }
        onBlur()
      }}
      onClick={event => {
        event.preventDefault()
        event.stopPropagation()
        onSelect({ id, key: entryKey, onSelectData })
      }}
      style={{
        height: ROW_HEIGHT,
        paddingLeft: 16 + depth * PADDING_LEFT_LEVEL_RATIO,
        paddingRight: 16,
      }}
    >
      <div
        className={classNames(
          'shade-30 flexbox flexbox-align-center primary-on-focus',
          {
            primary: isSelected,
          }
        )}
      >
        {icon}
      </div>
      <div className="flexbox-no-grow ellipsis margin-left-medium margin-right-small">
        {title}
      </div>
      <div className="flexbox-grow font-weight-regular ellipsis shade-20 margin-left-smaller">
        {detail}
      </div>

      {isExpandable && (
        <div
          className={classNames(
            'flexbox',
            'shade-30 shade-20-on-hover',
            'margin-left-smaller padding-smaller radius-normal background-shade-50-on-hover'
          )}
          onClick={event => {
            event.preventDefault()
            event.stopPropagation()
            onToggleExpand(id)
          }}
        >
          {isExpanded ? (
            <ChevronIcon size={12} className="rotate-clockwise" />
          ) : (
            <ChevronIcon size={12} />
          )}
        </div>
      )}
    </div>
  )
}

Entry.propTypes = {
  id: PropTypes.string.isRequired,
  entryKey: PropTypes.string.isRequired,
  depth: PropTypes.number.isRequired,
  icon: PropTypes.node,
  title: PropTypes.node.isRequired,
  detail: PropTypes.node,
  isExpandable: PropTypes.bool.isRequired,
  onSelectData: PropTypes.shape({
    channelId: PropTypes.string.isRequired,
    parentNoteId: PropTypes.string,
  }),

  isMeta: PropTypes.bool,
  renderBypass: PropTypes.func,

  isSelected: PropTypes.bool.isRequired,
  isFocused: PropTypes.bool.isRequired,
  isExpanded: PropTypes.bool.isRequired,
  onBlur: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
  onToggleExpand: PropTypes.func.isRequired,
  onFocusNextRow: PropTypes.func.isRequired,
  onFocusPreviousRow: PropTypes.func.isRequired,
}
